// Client work, normalised across the four systems it lives in.
//
// ADO and Monday come from our own API — they need credentials the browser
// must not hold. Client to-dos and implementation requests are fetched here
// with the signed-in user's own token from the services that already serve
// them, the same split the filing flow uses.
//
// Everything is flattened to ONE item shape so a view can show four systems in
// one list without carrying four sets of field names:
//
//   { source, id, url, title, status, priority, type, clientId, clientTitle,
//     owner, creator, requestor, stakeholders, createdOn, updatedOn, dueOn, open }
//
// requestor/stakeholders are real for every source now: Monday has a text and a
// People column, requests have real columns, and to-dos and ADO — neither of
// which has anywhere native to put them — are served from analytics' own
// item_metadata store, merged in server-side.

import { getUser } from '../auth/authService';
import { updateTodo, fetchAllTodos, fetchTodoNotes, createTodoNote, updateTodoNote } from '../Components/CS/ClientOverview/api';
import {
    updateSuccessRequest, getSuccessRequest, getSuccessRequests, getRequestNotes, createRequestNote,
    updateRequestNote,
    currentUserName,
} from './successRequestService';
import { tracked, itemRef, itemResource } from './workTelemetry';
import { normaliseWorkNote, normaliseTodoNote, normaliseRequestNote } from './noteShapes';

/** SQL Server's reading of a blank date. Treated as no date at all. */
export const isNullDate = (v) => String(v || '').startsWith('1900-01-01');

const ANALYTICS = `${process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? ''}`;

export const SOURCES = [
    { key: 'ado', label: 'DevOps', blurb: 'Engineering tickets in the Nexus project' },
    { key: 'monday', label: 'Outreach', blurb: 'Technical Outreach Requests to districts' },
    { key: 'todo', label: 'To-dos', blurb: 'CS tasks on a client' },
    { key: 'request', label: 'Requests', blurb: 'Projects and dashboard buildouts' },
    // Customer email threads the shared inboxes saw (outbound-only included), read
    // from the Analytics email sweep's ledger (ADO #4338). A fifth source,
    // added with Patrick's OK on 2026-10-19 ("each thread should be a
    // first-class work item").
    { key: 'email', label: 'Email', blurb: 'Customer email threads, from every Nexus mailbox' },
];

// The app's validated categorical palette (dataviz skill), first five slots —
// one per source, assigned in fixed order and never cycled. Duplicated rather
// than imported for the same reason the other chart surfaces duplicate it:
// the constant is not exported from any of them.
export const SOURCE_COLORS = {
    light: { ado: '#2a78d6', monday: '#eb6834', todo: '#1baf7a', request: '#eda100', email: '#e87ba4' },
    dark: { ado: '#3987e5', monday: '#d95926', todo: '#199e70', request: '#c98500', email: '#d55181' },
};

// ADO and Monday arrive already carrying `open` and `state_category`, decided
// server-side from ADO's own published state categories rather than from state
// names. The two SPA-side sources are mapped onto the same four categories
// here so one status chip means one thing across all five systems.
const REQUEST_CATEGORY = {
    open: 'Proposed',
    'in progress': 'InProgress',
    blocked: 'InProgress',
    completed: 'Completed',
    cancelled: 'Removed',
};

async function authHeaders() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${user.access_token}` };
}

/**
 * ADO + Monday, already normalised and client-resolved server-side.
 *
 * Always asks for closed work too. The endpoint defaults to open-only, and not
 * asking meant no closed DevOps or Outreach item ever reached the browser: the
 * "Closed"/"All" scope showed those two systems as empty, and the dashboard's
 * closed count and its closed bars were quietly made of to-dos and requests
 * alone — the only two sources the SPA fetches whole.
 *
 * Both surfaces already narrow by scope themselves, so the filtering belongs
 * there, where the reader can change it, rather than in a request they cannot
 * see. It is ~470 items; the round trip is one call either way.
 */
export async function getExternalWork({ clientId = '', refresh = false } = {}) {
    const qs = new URLSearchParams();
    qs.set('include_closed', 'true');
    if (clientId) qs.set('client_id', clientId);
    if (refresh) qs.set('refresh', 'true');
    const res = await fetch(`${ANALYTICS}/api/work/external?${qs}`, { headers: await authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
        attribution: data.attribution || {},
        items: (data.items || []).map((i) => ({
            source: i.source,
            id: i.id,
            url: i.url,
            title: i.title,
            status: i.status,
            priority: i.priority,
            type: i.type,
            clientId: i.client_id || null,
            // Every client the label could mean, and how confident that is:
            // 'resolved' | 'ambiguous' | 'multi' | 'none'. Ambiguous work shows
            // on each candidate's tab with a note; multi-client work shows only
            // in the fleet-wide dashboard.
            clientIds: i.client_ids || [],
            attribution: i.attribution || 'none',
            // Resolved to a real client whose row is marked inactive. Their
            // work is still their work; the views say "inactive" rather than
            // pretending the label is wrong or the client unknown.
            clientInactive: !!i.client_inactive,
            // DevOps records blocked as a tag, not a state. The whole tag list
            // comes through because the board also uses tags for Fast Track,
            // Client Audit and Done by Eve.
            tags: i.tags || [],
            // The metadata store behind older tickets' requestor/stakeholders
            // could not be read (work.py people_unreadable): unknown, not empty.
            peopleUnavailable: !!i.people_unreadable,
            // Work on the Super Portal itself (its DevOps area), decided
            // server-side (work.is_super_portal). Work overview hides it by default.
            superPortal: !!i.super_portal,
            // DevOps only: System.AreaPath, shown on the row as its last part (#4230).
            area: i.area || '',
            // The state it was in when it was resolved from these views, so
            // Reopen can offer where it was instead of a guess.
            priorState: i.prior_state || '',
            candidateTitles: i.candidate_titles || [],
            clientLabel: i.client_label || '',
            clientTitle: i.client_title || i.client_label || '',
            owner: i.owner,
            // ADO's real uniqueName / Monday's resolved person, "" otherwise.
            ownerEmail: i.owner_email || '',
            creator: i.creator || '',
            requestor: i.requestor || '',
            stakeholders: i.stakeholders || [],
            createdOn: i.created_on,
            updatedOn: i.updated_on,
            // When the item entered its CURRENT state. DevOps reports this
            // exactly (ADO's StateChangeDate); the other three have no
            // state-change timestamp, so they fall back to "last edited",
            // which counts any edit as movement. The footer says so.
            movedOn: i.moved_on || i.updated_on || '',
            movedIsExact: !!i.moved_on,
            dueOn: i.due_on,
            // Decided server-side; never re-derived here, or the two would drift.
            state_category: i.state_category || 'InProgress',
            open: !!i.open,
        })),
        errors: data.errors || [],
        clients: data.clients || [],
    };
}

/**
 * An email thread in the shared item shape. Status and category are decided
 * server-side from the thread's Outlook tag (Resolved closes it) and never
 * re-derived here. No deadline, no priority: the row says "cannot say".
 */
export const normaliseEmail = (i) => ({
    source: 'email',
    id: String(i.id),
    url: i.url || '',
    title: i.title || '(no subject)',
    status: i.status || 'Open',
    state_category: i.state_category || 'InProgress',
    priority: '',
    type: i.type || 'Email thread',
    clientId: i.client_id || null,
    clientIds: i.client_ids || [],
    attribution: i.attribution || 'none',
    clientInactive: !!i.client_inactive,
    candidateTitles: i.candidate_titles || [],
    clientLabel: i.client_label || '',
    clientTitle: i.client_title || i.client_label || '',
    // The Nexus person who last wrote on the thread (an address; the People
    // directory names it). Blank = nobody from us has replied.
    owner: i.owner || '',
    ownerEmail: i.owner_email || '',
    creator: i.creator || '',
    requestor: i.requestor || '',
    stakeholders: i.stakeholders || [],
    peopleUnavailable: false,
    tags: i.tags || [],
    // Decided server-side from the Outlook tag: a tag starting with "Waiting".
    blocked: !!i.blocked,
    // The first district sender, derived from the thread: shown as Client owner
    // (district) until someone sets one; informational, never contacted.
    clientOwnerDerived: i.client_owner || '',
    superPortal: false,
    area: '',
    priorState: '',
    createdOn: i.created_on || '',
    updatedOn: i.updated_on || i.created_on || '',
    // The tagged message's time when a tag decided the status; else last activity.
    movedOn: i.moved_on || i.updated_on || '',
    movedIsExact: !!i.moved_on,
    dueOn: '',
    open: !!i.open,
    email: i.email || {},
});

/**
 * Customer email threads as work (GET /api/work/email). Always asks for closed
 * threads too, like getExternalWork; the views narrow by scope themselves.
 * Carries `unavailable` (mailboxes the last sweep could not read) and `stale`
 * so a view can say so instead of showing a shorter list as the whole truth.
 */
export async function getEmailWork({ clientId = '', refresh = false } = {}) {
    const qs = new URLSearchParams();
    qs.set('include_closed', 'true');
    if (clientId) qs.set('client_id', clientId);
    if (refresh) qs.set('refresh', 'true');
    const res = await fetch(`${ANALYTICS}/api/work/email?${qs}`, { headers: await authHeaders() });
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.detail || ''; } catch { /* no body */ }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return {
        items: (data.items || []).map(normaliseEmail),
        unavailable: data.unavailable || [],
        stale: !!data.stale,
        asOf: data.asOf || '',
        errors: data.errors || [],
    };
}

/**
 * Repairs links stored before the route was corrected.
 *
 * The filing ledger is append-only — it is the audit trail of what was filed
 * where and by whom — so a wrong URL written into it stays written. Rewriting
 * history to fix a typo is the wrong trade; normalising on the way out costs
 * nothing and keeps the record honest.
 *
 * "/cs/client-overview" was never a route: the catch-all redirected it to the
 * home page, so every to-do filed before the fix has a link that silently goes
 * nowhere.
 */
export const repairInternalUrl = (url) => (
    typeof url === 'string' ? url.replace('/cs/client-overview?', '/cs?') : url
);

/** What the CS digest raised and what came of it. */
export async function getDigestFollowUps(days = 30) {
    const res = await fetch(`${ANALYTICS}/api/work/followups?days=${days}`, {
        headers: await authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

// One directory, shared by every picker on the page (to-do form, request
// form, etc.) rather than one fetch per form mount. Cleared on failure so a
// transient outage doesn't cache "no staff" for the rest of the session.
let staffDirectoryPromise = null;

/**
 * Internal Nexus staff, for pre-populating owner/requestor/stakeholder
 * pickers with real people — so picking one stores their actual email
 * instead of a free-text name the cadence job would later have to
 * guess-match. Always resolves; a directory outage just means an empty
 * list and the pickers fall back to free-text entry.
 */
export function getStaffDirectory() {
    if (!staffDirectoryPromise) {
        staffDirectoryPromise = (async () => {
            const res = await fetch(`${ANALYTICS}/api/work/directory`, { headers: await authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return data.staff || [];
        })().catch((e) => { staffDirectoryPromise = null; throw e; });
    }
    return staffDirectoryPromise.catch(() => []);
}

// requestedBy/stakeholders are NOT on the to-do itself (management.
// NexusClientTodo has no such column, and stays that way — see
// tools_api/cs_digest/filing/item_metadata.py). `meta` is this item's entry
// from getTodoMetadata()'s bulk map, merged in by the caller.
export const normaliseTodo = (t, meta = {}) => ({
    source: 'todo',
    id: String(t.id),
    // /cs is the Client Overview route; it reads ?client and ?todo and opens
    // that to-do. "/cs/client-overview" is not a route at all — the catch-all
    // silently redirected every to-do link to the home page.
    url: `/cs?client=${t.clientId}&todo=${t.id}`,
    title: t.title || '',
    status: t.isDeleted ? 'Deleted' : t.done ? 'Done' : 'Open',
    state_category: t.isDeleted ? 'Removed' : t.done ? 'Completed' : 'InProgress',
    priority: t.priority || '',
    type: 'To-do',
    clientId: t.clientId || null,
    clientIds: t.clientId ? [t.clientId] : [],
    attribution: t.clientId ? 'resolved' : 'none',
    clientInactive: false,
    candidateTitles: t.clientName ? [t.clientName] : [],
    clientLabel: t.clientName || '',
    clientTitle: t.clientName || '',
    // Owner is who does the work (assignedTo). "Check-in with" is a different
    // fact -- who the CSM checks in with -- and was being shown as the owner
    // whenever assignedTo was blank, which read "Nexus Implementations Team"
    // as an owner on rows nobody owned.
    owner: t.assignedTo || '',
    checkinWith: t.checkinWith || '',
    creator: t.createdBy || '',
    requestor: meta.requested_by || '',
    stakeholders: meta.stakeholders || [],
    // The metadata store could not be read: requestor/stakeholders are
    // unknown, not empty (WorkPeople says so).
    peopleUnavailable: !!meta.unavailable,
    createdOn: t.createdOn || '',
    updatedOn: t.updatedOn || t.createdOn || '',
    movedOn: t.updatedOn || t.createdOn || '',
    movedIsExact: false,
    dueOn: t.checkinDate && !isNullDate(t.checkinDate) ? t.checkinDate : '',
    open: !t.done && !t.isDeleted,
});

export const normaliseRequest = (r) => ({
    source: 'request',
    id: String(r.NexusSuccessRequest_Id ?? r.Id ?? ''),
    url: `/implementations?request=${r.NexusSuccessRequest_Id ?? ''}`,
    title: r.Title || '',
    status: r.Status || '',
    state_category: REQUEST_CATEGORY[(r.Status || '').toLowerCase()] || 'InProgress',
    priority: r.Priority || '',
    type: r.RequestType || '',
    clientId: r.NexusClient_Id || null,
    clientIds: r.NexusClient_Id ? [r.NexusClient_Id] : [],
    attribution: r.NexusClient_Id ? 'resolved' : 'none',
    clientInactive: false,
    candidateTitles: r.ClientName ? [r.ClientName] : [],
    clientLabel: r.ClientName || '',
    clientTitle: r.ClientName || '',
    // Owner is AssignedTo only: falling back to RequestedBy hid unassigned
    // requests behind their requester's name.
    owner: r.AssignedTo || '',
    creator: r.CreatedBy || '',
    requestor: r.RequestedBy || '',
    stakeholders: r.Stakeholders || [],
    createdOn: r.CreatedOn || '',
    updatedOn: r.UpdatedOn || r.CreatedOn || '',
    movedOn: r.UpdatedOn || r.CreatedOn || '',
    movedIsExact: false,
    dueOn: r.DueDate && !isNullDate(r.DueDate) ? r.DueDate : '',
    open: !['completed', 'cancelled'].includes((r.Status || '').toLowerCase()),
});

/**
 * Requestor/stakeholders for every to-do that has ever had one saved.
 * {item_id -> {requested_by, stakeholders}} — analytics' own store, not
 * anything Chat's to-do API returns.
 */
// The store keys ids by item_metadata.canonical_id() (trimmed, lower-case);
// SQL and some APIs hand back upper-case GUIDs. Every lookup goes through
// here, so a casing difference can never read as "nobody recorded".
export const todoMetaKey = (id) => String(id ?? '').trim().toLowerCase();

/**
 * The bulk to-do metadata, or an explicit "couldn't read" -- never a silent
 * empty map. `unavailable` rows must not be edited as if they had no
 * requestor/stakeholders: saving them would wipe what is stored.
 */
export async function loadTodoMetadata() {
    try {
        return { byId: await getTodoMetadata(), unavailable: false };
    } catch (e) {
        return { byId: {}, unavailable: true, error: e.message };
    }
}

/** One to-do's {requested_by, stakeholders} from loadTodoMetadata(), or {unavailable: true}. */
export const todoMetaFor = (meta, id) => (meta?.unavailable ? { unavailable: true }
    : (meta?.byId || {})[todoMetaKey(id)] || {});

export async function getTodoMetadata() {
    const res = await fetch(`${ANALYTICS}/api/work/todo/metadata`, { headers: await authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.items || {};
}

/** One to-do's requestor/stakeholders — for prefilling its edit form. */
export async function getOneTodoMetadata(itemId) {
    const res = await fetch(`${ANALYTICS}/api/work/todo/${encodeURIComponent(itemId)}/metadata`, {
        headers: await authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json(); // { requested_by, stakeholders }
}

/** Save one to-do's requestor/stakeholders. */
async function saveTodoMetadataUntracked(itemId, { requestedBy = '', stakeholders = [] } = {}) {
    const res = await fetch(`${ANALYTICS}/api/work/todo/${encodeURIComponent(itemId)}/metadata`, {
        method: 'PUT', headers: await authHeaders(),
        body: JSON.stringify({ requested_by: requestedBy, stakeholders }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    return res.json();
}

/**
 * Every source, fetched INDEPENDENTLY so a view can paint each as it lands.
 *
 * Returns one promise per source rather than a single combined promise. That
 * matters for more than speed: ADO alone is a WIQL plus three batch calls, so
 * a combined await leaves the page blank for seconds — and a view that
 * rendered partial state would show "0 DevOps items" for a source that simply
 * has not arrived yet, which reads as "there is no work" rather than "not
 * loaded". The caller tracks per-source state and shows a number only once
 * that source has actually answered.
 *
 * Each promise RESOLVES on failure, carrying the error, so one system being
 * down degrades the view instead of rejecting the whole load.
 */
export function fetchWorkBySource({ clientId = '', refresh = false } = {}) {
    const settle = (source, run) => run().then(
        (items) => ({ source, items, error: null }),
        (e) => ({ source, items: [], error: e.message }),
    );

    return {
        // ADO and Monday arrive together — they are one round trip to our API.
        external: getExternalWork({ clientId, refresh }).then(
            (d) => ({ source: 'external', items: d.items, clients: d.clients,
                      errors: d.errors, counts: d.counts,
                      attribution: d.attribution, error: null }),
            (e) => ({ source: 'external', items: [], clients: [], errors: [],
                      counts: {}, attribution: {}, error: e.message }),
        ),
        todo: settle('todo', async () => {
            // Independent calls, not a dependent chain: a metadata-store
            // hiccup must not also blank out the to-do list, so it degrades
            // to "no requestor/stakeholders shown" rather than failing here.
            const [todos, metadata] = await Promise.all([
                fetchAllTodos().then((d) => d?.todos ?? []),
                loadTodoMetadata(),
            ]);
            return todos.map((t) => normaliseTodo(t, todoMetaFor(metadata, t.id)));
        }),
        request: settle('request', async () => (await getSuccessRequests()).map(normaliseRequest)),
        // Email threads, from Analytics. A mailbox the sweep could not read is
        // reported on the part, not folded into "no threads".
        email: getEmailWork({ clientId, refresh }).then(
            (d) => ({ source: 'email', items: d.items, error: null,
                      unavailable: d.unavailable, stale: d.stale, asOf: d.asOf }),
            (e) => ({ source: 'email', items: [], error: e.message, unavailable: [], stale: false, asOf: '' }),
        ),
    };
}

/**
 * Does this item belong on `clientId`'s tab?
 *
 * Case-insensitive on purpose: the same client arrives spelled two ways — the
 * analytics client list lower-cases the GUID, while Client Overview passes the
 * upper-case form SQL hands it. A `===` matched neither, and every client's
 * Work tab rendered "Nothing open here" over a full set of live work.
 *
 * Matches on `clientIds`, never on the single `clientId`: an ambiguous item has
 * no single client, and testing the scalar would drop it from both candidates'
 * tabs — the one place a CSM could recognise and correct the label.
 */
export function belongsToClient(item, clientId) {
    if (!clientId) return true;
    const target = String(clientId).toLowerCase();
    return (item.clientIds || []).some((id) => String(id).toLowerCase() === target);
}

/**
 * A mailbox the email sweep could not read is a hole in the email list, not an
 * empty mailbox. Every view that merges the email part reports it the same way.
 */
export const emailPartialErrors = (part) => (
    (part?.unavailable || []).length
        ? [{ source: 'email', partial: true,
             error: `couldn't read ${part.unavailable.join(', ')}; email threads from there may be missing` }]
        : []
);

/** Convenience wrapper for callers that genuinely want everything at once. */
export async function getAllWork({ clientId = null, refresh = false } = {}) {
    const parts = fetchWorkBySource({ clientId: clientId || '', refresh });
    const [external, todos, requests, email] = await Promise.all([
        parts.external, parts.todo, parts.request, parts.email,
    ]);
    const errors = [
        ...(external.errors || []),
        ...[external, todos, requests, email]
            .filter((p) => p.error)
            .map((p) => ({ source: p.source, error: p.error })),
        ...emailPartialErrors(email),
    ];
    let items = [...external.items, ...todos.items, ...requests.items, ...email.items];
    if (clientId) items = items.filter((i) => belongsToClient(i, clientId));
    return { items, clients: external.clients || [], errors, counts: external.counts || {} };
}

// ---------------------------------------------------------------------------
// Resolving and reopening
// ---------------------------------------------------------------------------

const statesCache = new Map();

/**
 * What resolve and reopen can mean for one system, from that system's own
 * schema — DevOps' two terminal states are read live from the project.
 *
 * Cached per system and work-item type: a dense list asks for the same handful
 * of answers hundreds of times.
 */
export async function getTransitionStates(source, workItemType = '') {
    const key = `${source}|${workItemType}`;
    if (!statesCache.has(key)) {
        statesCache.set(key, (async () => {
            const qs = new URLSearchParams({ source });
            if (workItemType) qs.set('work_item_type', workItemType);
            const res = await fetch(`${ANALYTICS}/api/work/states?${qs}`, {
                headers: await authHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        })().catch((e) => { statesCache.delete(key); throw e; }));
    }
    return statesCache.get(key);
}

/** Record a to-do or request the SPA changed itself, into the shared ledger. */
const recordTransition = async (payload) => fetch(`${ANALYTICS}/api/work/transitions`, {
    method: 'POST', headers: await authHeaders(), body: JSON.stringify(payload),
});

/**
 * Move one item to `toState`, whichever system it lives in.
 *
 * DevOps and Monday go through our API, which holds the credentials and reads
 * the item's current state immediately before writing — the previous state
 * comes from the source system, not from what this page believed a minute ago.
 *
 * To-dos and requests are changed here with the signed-in user's own token,
 * the same split as filing, and then recorded so all four share one audit
 * trail and a reopen can restore a request's free-form status.
 */
async function transitionWorkUntracked({ item, action, toState }) {
    // DevOps, Monday and email threads go through our API (Outlook tags are
    // written with the Graph app's credentials, like the other two).
    if (item.source === 'ado' || item.source === 'monday' || item.source === 'email') {
        const res = await fetch(
            `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/transition`,
            {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({
                    action, to_state: toState, type: item.type,
                    title: item.title, client_title: item.clientTitle,
                }),
            },
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        return res.json();
    }

    const fromState = item.status || '';
    if (item.source === 'todo') {
        await updateTodo(item.clientId, item.id, { done: action === 'resolve' });
    } else if (item.source === 'request') {
        // The Integrations PUT is a FULL-record update whose body model
        // requires title, request_type, priority, status and updated_by, and
        // whose UPDATE statement writes every column it carries. Sending
        // `{ Status }` alone -- what this did until 2026-09-22 -- failed
        // validation (422) on every resolve and reopen of a request from these
        // views; had it validated, it would have blanked the other columns.
        // So: read the row, change one field, write the whole row back.
        await updateRequestFields(item.id, { status: toState });
    } else {
        throw new Error(`${item.source} cannot be changed from here`);
    }

    // The write succeeded; a failed audit line must not look like a failed
    // change, so this is best-effort and logged rather than thrown.
    await recordTransition({
        source: item.source, item_id: item.id, action,
        from_state: fromState, to_state: toState,
        title: item.title, client_title: item.clientTitle,
    }).catch(() => {});
    return { ok: true, from_state: fromState, to_state: toState, action };
}

/**
 * Change some fields on a success request without losing the rest.
 *
 * The Integrations service's PUT /success-requests/{id} replaces the whole
 * row from the body (see its update_success_request), so the current row is
 * read first and every column is sent back with only `changes` applied.
 * `changes` uses the API's snake_case body names (status, assigned_to,
 * requested_by, stakeholders, due_on, ...).
 */
async function updateRequestFieldsUntracked(requestId, changes) {
    const r = await getSuccessRequest(requestId);
    const row = r?.request ?? r;
    if (!row || !row.Title) throw new Error(`Request ${requestId} could not be read before updating it`);
    const body = {
        title: row.Title,
        request_type: row.RequestType,
        priority: row.Priority,
        status: row.Status,
        nexus_client_id: row.NexusClient_Id,
        description: row.Description,
        details: row.Details,
        requested_by: row.RequestedBy,
        assigned_to: row.AssignedTo,
        resolution: row.Resolution,
        // null, never '': SQL Server stores '' in a DATETIME as 1900-01-01,
        // which turned an undated request 46,000 days overdue (2026-09-22).
        due_on: row.DueDate && !isNullDate(row.DueDate) ? String(row.DueDate).slice(0, 10) : null,
        stakeholders: row.Stakeholders || [],
        ...changes,
        updated_by: await currentUserName(),
    };
    return updateSuccessRequest(requestId, body);
}

// ---------------------------------------------------------------------------
// People on an item — owner, requestor, stakeholders
// ---------------------------------------------------------------------------

/**
 * Who Azure DevOps will accept in System.AssignedTo: the org roster
 * (vssps graph users with an email principal), NOT the Nexus staff
 * directory. Offering a Nexus superuser ADO does not hold lost six tickets
 * on 2026-09-22. Empty when the roster cannot be read; callers fall back to
 * the staff directory and let the server decide.
 */
let adoAssigneesPromise = null;
export function getAdoAssignees() {
    if (!adoAssigneesPromise) {
        adoAssigneesPromise = (async () => {
            const res = await fetch(`${ANALYTICS}/api/work/ado/assignees`, { headers: await authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return (await res.json()).assignees || [];
        })().catch((e) => { adoAssigneesPromise = null; throw e; });
    }
    return adoAssigneesPromise.catch(() => []);
}

/**
 * Everyone with a Monday seat, [{email, name}]: the only people the board's
 * Owner and Stakeholders People columns can hold, so Monday pickers draw
 * from this rather than from the Nexus directory.
 */
let mondayPeoplePromise = null;
export function getMondayPeople() {
    if (!mondayPeoplePromise) {
        mondayPeoplePromise = (async () => {
            const res = await fetch(`${ANALYTICS}/api/work/monday/people`, { headers: await authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return (await res.json()).people || [];
        })().catch((e) => { mondayPeoplePromise = null; throw e; });
    }
    return mondayPeoplePromise.catch(() => []);
}

/**
 * Set owner / requested by / stakeholders on one item, in its own system.
 *
 *   ado      POST /api/work/ado/{id}/people — System.AssignedTo (roster-checked;
 *            an address ADO does not hold is refused, not silently dropped)
 *            plus the item-metadata ledger for requestor/stakeholders.
 *   monday   POST /api/work/monday/{id}/people — Technical owner (a status
 *            column: Patrick or Jackson only), Requested by text, Stakeholders
 *            People column (emails resolved to Monday ids; no seat = dropped).
 *   todo     PUT assignedTo with your token, then the item-metadata ledger.
 *   request  Full-row PUT with assigned_to / requested_by / stakeholders.
 *
 * `people` = { owner, requestedBy, stakeholders }; an undefined key is left
 * as it is, an empty string clears it.
 */
async function setWorkPeopleUntracked(item, people) {
    const { owner, requestedBy, stakeholders } = people;
    if (item.source === 'ado' || item.source === 'monday') {
        const res = await fetch(
            `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/people`,
            { method: 'POST', headers: await authHeaders(), body: JSON.stringify({
                ...(owner !== undefined ? { owner } : {}),
                ...(requestedBy !== undefined ? { requested_by: requestedBy } : {}),
                ...(stakeholders !== undefined ? { stakeholders } : {}),
            }) },
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        return res.json();
    }
    if (item.source === 'todo') {
        if (owner !== undefined) await updateTodo(item.clientId, item.id, { assignedTo: owner });
        if (requestedBy !== undefined || stakeholders !== undefined) {
            const cur = (requestedBy === undefined || stakeholders === undefined)
                ? await getOneTodoMetadata(item.id).catch(() => ({ requested_by: '', stakeholders: [] }))
                : {};
            await saveTodoMetadata(item.id, {
                requestedBy: requestedBy !== undefined ? requestedBy : cur.requested_by,
                stakeholders: stakeholders !== undefined ? stakeholders : cur.stakeholders,
            });
        }
        return { ok: true };
    }
    if (item.source === 'request') {
        await updateRequestFields(item.id, {
            ...(owner !== undefined ? { assigned_to: owner } : {}),
            ...(requestedBy !== undefined ? { requested_by: requestedBy } : {}),
            ...(stakeholders !== undefined ? { stakeholders } : {}),
        });
        return { ok: true };
    }
    throw new Error(`${item.source} people cannot be set from here`);
}

// ---------------------------------------------------------------------------
// Notes — the update log
// ---------------------------------------------------------------------------
//
// To-dos and requests already have their own notes tables/endpoints (reached
// with the caller's own token, same split as everywhere else here); ADO and
// Monday go through our API, which also best-effort mirrors the note into the
// source system's own comment/update thread. Every shape below is normalised
// (services/noteShapes.js) to { id, text, by, at, mine, editedAt, editedBy,
// history, mirror } so a view can render one note list regardless of source.

export async function getNotes(item) {
    if (item.source === 'ado' || item.source === 'monday') {
        const res = await fetch(
            `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/notes`,
            { headers: await authHeaders() },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return (data.notes || []).map(normaliseWorkNote);
    }
    if (item.source === 'todo') {
        const data = await fetchTodoNotes(item.id);
        return (data.notes || []).map(normaliseTodoNote);
    }
    if (item.source === 'request') {
        const rows = await getRequestNotes(item.id);
        return rows.map(normaliseRequestNote);
    }
    throw new Error(`${item.source} has no notes`);
}

/** Edit a note you posted (ADO #4318); resolves to the item's notes, like
 *  addNote. Only the author can: the server refuses anyone else. */
async function editNoteUntracked(item, noteId, text) {
    if (item.source === 'ado' || item.source === 'monday') {
        const res = await fetch(
            `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/notes/${encodeURIComponent(noteId)}`,
            { method: 'PUT', headers: await authHeaders(), body: JSON.stringify({ text }) },
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        return getNotes(item);
    }
    if (item.source === 'todo') {
        await updateTodoNote(item.id, noteId, text);
        return getNotes(item);
    }
    if (item.source === 'request') {
        await updateRequestNote(item.id, noteId, text);
        return getNotes(item);
    }
    throw new Error(`${item.source} has no notes`);
}

async function addNoteUntracked(item, text) {
    if (item.source === 'ado' || item.source === 'monday') {
        const res = await fetch(
            `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/notes`,
            { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ text }) },
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail || `HTTP ${res.status}`);
        }
        return getNotes(item);
    }
    if (item.source === 'todo') {
        await createTodoNote(item.id, text);
        return getNotes(item);
    }
    if (item.source === 'request') {
        const by = await currentUserName();
        await createRequestNote(item.id, text, by);
        return getNotes(item);
    }
    throw new Error(`${item.source} has no notes`);
}


// ---------------------------------------------------------------------------
// Links between work items
// ---------------------------------------------------------------------------

export const workKey = (item) => `${item.source}:${item.id}`;

// One shared copy of the link map. Every row's action bar needs to know whether
// its item has links, and a list of four hundred rows must not ask four hundred
// times -- so the first caller fetches, everyone shares the promise, and a
// change anywhere notifies every row that is listening.
let linkState = { byItem: {}, relations: [], loaded: false };
let linkFetch = null;
const linkListeners = new Set();

const publishLinks = (next) => {
    linkState = { ...linkState, ...next, loaded: true };
    linkListeners.forEach((fn) => fn(linkState));
};

export function subscribeWorkLinks(fn) {
    linkListeners.add(fn);
    return () => linkListeners.delete(fn);
}

export const currentWorkLinks = () => linkState;

export function loadWorkLinks({ refresh = false } = {}) {
    if (linkFetch && !refresh) return linkFetch;
    linkFetch = (async () => {
        const res = await fetch(`${ANALYTICS}/api/work/links`, { headers: await authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        publishLinks({ byItem: data.by_item || {}, relations: data.relations || [] });
        return linkState;
    })().catch((e) => { linkFetch = null; throw e; });
    return linkFetch;
}

/** The state a to-do or request was in before it was last resolved from
 *  these views ('' when there is no record). DevOps and Monday rows already
 *  carry it as item.priorState. */
export async function getPriorState(item) {
    try {
        const res = await fetch(`${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/prior-state`,
            { headers: await authHeaders() });
        if (!res.ok) return '';
        return (await res.json()).prior_state || '';
    } catch { return ''; }
}

/** Link `item` to `other`. `relation` reads from `item`: item BLOCKS other. */
async function linkWorkUntracked(item, other, relation, note = '') {
    const res = await fetch(
        `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/links`,
        { method: 'POST', headers: await authHeaders(), body: JSON.stringify({
            to_source: other.source, to_id: String(other.id), relation, note,
            from_title: item.title || '', to_title: other.title || '',
            from_url: item.url || '', to_url: other.url || '',
        }) },
    );
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    publishLinks({ byItem: (await res.json()).by_item || {} });
}

async function unlinkWorkUntracked(item, other) {
    const qs = `to_source=${encodeURIComponent(other.source)}&to_id=${encodeURIComponent(other.id)}`;
    const res = await fetch(
        `${ANALYTICS}/api/work/${item.source}/${encodeURIComponent(item.id)}/links?${qs}`,
        { method: 'DELETE', headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    publishLinks({ byItem: (await res.json()).by_item || {} });
}

// Every item across the four systems, for the "link to" picker. Shared and
// short-lived for the same reason as the link map: the picker opens from any
// row, and each open must not refetch four systems.
let allWorkFetch = null;
let allWorkAt = 0;
export function loadLinkCandidates() {
    if (allWorkFetch && Date.now() - allWorkAt < 120000) return allWorkFetch;
    allWorkAt = Date.now();
    allWorkFetch = getAllWork().then((d) => d.items || [])
        .catch((e) => { allWorkFetch = null; throw e; });
    return allWorkFetch;
}

/**
 * Cost of work: capacity shares, and cost to serve when a day rate is given.
 *
 * 503 means the collector has never completed a run -- it backfills one API
 * call per commit and is a job, not a request. That is a state the page shows,
 * not an error it hides.
 */
export async function getCostOverview({ dayRate = 0 } = {}) {
    const qs = dayRate > 0 ? `?day_rate=${encodeURIComponent(dayRate)}` : '';
    const res = await fetch(`${ANALYTICS}/api/cost/overview${qs}`,
        { headers: await authHeaders() });
    if (res.status === 503) {
        const body = await res.json().catch(() => ({}));
        return { pending: true, detail: body.detail || 'No cost snapshot yet.' };
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

/**
 * What each source contributes and what it structurally cannot -- the same
 * facts the /work/cost page's methodology section renders, from the API
 * rather than hard-coded prose, so the explanation can never drift from the
 * numbers it is describing.
 */
/** The one-screen weekly review: GET /api/cost/review?weeks=4|12. */
export async function getCostReview({ weeks = 4 } = {}) {
    const res = await fetch(`${ANALYTICS}/api/cost/review?weeks=${weeks}`,
        { headers: await authHeaders() });
    if (res.status === 503) {
        const body = await res.json().catch(() => ({}));
        return { pending: true, detail: body.detail || 'No cost snapshot yet.' };
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

export async function getCostSources() {
    const res = await fetch(`${ANALYTICS}/api/cost/sources`, { headers: await authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

// --- Telemetry: every write above is recorded as a work_* event (services/workTelemetry.js).
export const saveTodoMetadata = tracked('update_metadata', saveTodoMetadataUntracked, ([id, m]) => ({ resource: `todo:${id}`, details: { source: 'todo', item_id: String(id), requested_by: m?.requestedBy, stakeholders: m?.stakeholders } }));
export const transitionWork = tracked('transition', transitionWorkUntracked, ([{ item, action, toState }], r) => ({ resource: itemResource(item), details: { ...itemRef(item), action, from_state: r?.from_state ?? item?.status, to_state: r?.to_state ?? toState } }));
export const updateRequestFields = tracked('update', updateRequestFieldsUntracked, ([id, changes]) => ({ resource: `request:${id}`, details: { source: 'request', item_id: String(id), changes } }));
export const setWorkPeople = tracked('set_people', setWorkPeopleUntracked, ([item, people]) => ({ resource: itemResource(item), details: { ...itemRef(item), ...people } }));
export const editNote = tracked('note_edit', editNoteUntracked, ([item, noteId, text]) => ({ resource: itemResource(item), details: { ...itemRef(item), note_id: String(noteId), note: String(text || '').slice(0, 1000) } }));
export const addNote = tracked('note', addNoteUntracked, ([item, text]) => ({ resource: itemResource(item), details: { ...itemRef(item), note: String(text || '').slice(0, 1000) } }));
export const linkWork = tracked('link', linkWorkUntracked, ([item, other, relation, note]) => ({ resource: itemResource(item), details: { ...itemRef(item), relation, to: itemRef(other), note } }));
export const unlinkWork = tracked('unlink', unlinkWorkUntracked, ([item, other]) => ({ resource: itemResource(item), details: { ...itemRef(item), to: itemRef(other) } }));

/**
 * The weekly roadmap tidy-up's proposed parent links (Analytics
 * roadmap_tidy). Approver-only on the server; a 403 means this person
 * cannot approve, a 503 that the run could not be read (never "nothing
 * to approve").
 */
export async function getRoadmapTidyRun() {
    const res = await fetch(`${ANALYTICS}/api/work/roadmap-tidy`, { headers: await authHeaders() });
    if (!res.ok) {
        const err = new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/** links: [{item, epic}]. Resolves {results: [{item, epic, ok, reason?}]}. */
export async function approveRoadmapLinks(runId, links) {
    const res = await fetch(`${ANALYTICS}/api/work/roadmap-tidy/approve`, {
        method: 'POST', headers: await authHeaders(), body: JSON.stringify({ run_id: runId, links }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(body.detail || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return body;
}

/**
 * One team's roadmap: stage Epics and the Alongside lane, read live from
 * DevOps. `key` picks the roadmap (#4230); omitted, the server's default
 * (Super Portal). The body also lists every roadmap DevOps has
 * (`roadmaps`), or null when that list couldn't be read.
 */
export async function getRoadmap(key = '') {
    const qs = key ? `?roadmap=${encodeURIComponent(key)}` : '';
    const res = await fetch(`${ANALYTICS}/api/work/roadmap${qs}`, { headers: await authHeaders() });
    if (!res.ok) {
        const err = new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/** Ready for me (/work/ready): open DevOps tickets with a signal that access or data arrived. */
export async function getReady() {
    const res = await fetch(`${ANALYTICS}/api/work/ready`, { headers: await authHeaders() });
    if (!res.ok) {
        const err = new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/** Newest access / credentials / data signal per DevOps ticket (row tags). */
export async function getAccessSignals() {
    const res = await fetch(`${ANALYTICS}/api/work/ready/signals`, { headers: await authHeaders() });
    if (!res.ok) {
        const err = new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}
